/**
 * Central place for client-visible configuration (VITE_* only).
 * Never put secrets here — anything VITE_* is embedded in the browser bundle.
 */

function trim(s: string | undefined): string {
  return (s ?? '').trim()
}

function isLocalHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
}

/** Production Supabase is HTTPS. Local CLI (`http://127.0.0.1:54321`) is allowed in dev only. */
function isAllowedSupabaseProjectUrl(s: string): boolean {
  try {
    const u = new URL(s)
    if (u.protocol === 'https:') return true
    if (import.meta.env.DEV && u.protocol === 'http:' && isLocalHostname(u.hostname)) return true
    return false
  } catch {
    return false
  }
}

export type AppEnv = {
  /** Supabase project URL (public). */
  supabaseUrl: string
  /** Supabase anon key (public; protected by RLS). */
  supabaseAnonKey: string
  /** True when URL/key look usable — avoids blank clients in production. */
  isSupabaseConfigured: boolean
  isDev: boolean
  isProd: boolean
  /** Opt-in verbose client logs (beta debugging). */
  enableClientLogs: boolean
  /**
   * Training partners + compare surfaces. Default true; set `VITE_ENABLE_SOCIAL_FEATURES=false`
   * to hide/compare routes for a minimal beta.
   */
  enableSocialFeatures: boolean
  /**
   * Form-check video clip upload from the workout screen. Default true; set false if storage
   * is not configured or the flow is not ready for testers.
   */
  enableFormClipUpload: boolean
  /**
   * Hosted drill video uploads (Cloudflare Stream). Default off — must be explicit `true` plus
   * {@link AppEnv.videoUploadProvider} `cloudflare_stream` before UI enables upload scaffolding.
   */
  enableCloudflareStreamUploads: boolean
  /**
   * Future: which hosted upload backend to use (`cloudflare_stream`, etc.).
   * Mirrors optional `NEXT_PUBLIC_VIDEO_UPLOAD_PROVIDER` for cross-tooling; Vite uses `VITE_*`.
   */
  videoUploadProvider: string | null
}

function readEnv(): AppEnv {
  const supabaseUrl = trim(import.meta.env.VITE_SUPABASE_URL as string | undefined)
  const supabaseAnonKey = trim(import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)

  const isSupabaseConfigured = Boolean(
    supabaseUrl && supabaseAnonKey && isAllowedSupabaseProjectUrl(supabaseUrl) && supabaseAnonKey.length >= 32,
  )

  const enableClientLogs =
    import.meta.env.DEV ||
    trim(import.meta.env.VITE_ENABLE_CLIENT_LOGS as string | undefined).toLowerCase() === 'true'

  const envVarIsFalse = (key: string) =>
    trim((import.meta.env as Record<string, string | undefined>)[key]).toLowerCase() === 'false'

  const envVarIsTrue = (key: string) =>
    trim((import.meta.env as Record<string, string | undefined>)[key]).toLowerCase() === 'true'

  const viteEnv = import.meta.env as Record<string, string | undefined>
  const videoUploadProviderRaw =
    trim(viteEnv.VITE_VIDEO_UPLOAD_PROVIDER) || trim(viteEnv.NEXT_PUBLIC_VIDEO_UPLOAD_PROVIDER)

  return {
    supabaseUrl,
    supabaseAnonKey,
    isSupabaseConfigured,
    isDev: import.meta.env.DEV,
    isProd: import.meta.env.PROD,
    enableClientLogs,
    enableSocialFeatures: !envVarIsFalse('VITE_ENABLE_SOCIAL_FEATURES'),
    enableFormClipUpload: !envVarIsFalse('VITE_ENABLE_FORM_CLIP_UPLOAD'),
    enableCloudflareStreamUploads: envVarIsTrue('VITE_ENABLE_CLOUDFLARE_STREAM_UPLOADS'),
    videoUploadProvider: videoUploadProviderRaw ? videoUploadProviderRaw.toLowerCase() : null,
  }
}

/** Singleton — read once at module load (Vite inlines import.meta.env). */
export const env = readEnv()
